import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Textarea,
} from '@data-delivery/d3s';
import type { SSHKeyEntity, UserEntity } from '@data-delivery/permission-ui-api';
import {
  useAddSshKeyToUserMutation,
  useEditSshKeyForUserMutation,
} from '@data-delivery/permission-ui-data-access-state';
import { zodResolver } from '@hookform/resolvers/zod';

import { useForm } from 'react-hook-form';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';
import { showErrorToast } from '../../utils/error-utils';

interface BaseProps {
  user: UserEntity;
}

const sshKeySchema = z.object({
  key: z.string().min(10, 'Public key too short'),
  algorithm: z.string().min(3, 'Algorithm required'),
  comments: z.string().optional().default(''),
});
type SshKeyFormValues = z.infer<typeof sshKeySchema>;

export function UserSshKeyCreateView({ user }: BaseProps) {
  const addKey = useAddSshKeyToUserMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const { businessGroupId } = useParams();

  const form = useForm<SshKeyFormValues>({
    resolver: zodResolver(sshKeySchema),
    defaultValues: { key: '', algorithm: '', comments: '' },
    mode: 'onBlur',
  });

  return (
    <div className='flex flex-col gap-3'>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(values => {
            const entity: SSHKeyEntity = {
              key: values.key.trim(),
              algorithm: values.algorithm.trim(),
              comments: values.comments?.trim() || '',
              identifier: 'optimistic-temp',
            };
            addKey.mutate(
              { userId: user.id, key: entity },
              {
                onSuccess() {
                  toast.success('SSH key created');
                  if (businessGroupId) {
                    navigate(`/business-groups/${businessGroupId}/users/${user.id}/ssh-keys`, {
                      state: {
                        backgroundLocation: location.state?.backgroundLocation || location,
                      },
                    });
                  }
                },
                onError(err: unknown) {
                  showErrorToast(err);
                },
              },
            );
          })}
          className='flex flex-col gap-4'
        >
          <FormField
            control={form.control}
            name='key'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Public SSH Key</FormLabel>
                <FormControl>
                  <Textarea rows={5} placeholder='Paste public key' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='algorithm'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Algorithm</FormLabel>
                <FormControl>
                  <Input placeholder='e.g. ssh-rsa' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='comments'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Comments</FormLabel>
                <FormControl>
                  <Input placeholder='Optional description' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className='flex gap-2'>
            <Button size='sm' type='submit' disabled={addKey.isPending}>
              {addKey.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant='outline'
              size='sm'
              type='button'
              onClick={() => {
                if (businessGroupId) {
                  navigate(`/business-groups/${businessGroupId}/users/${user.id}/ssh-keys`, {
                    state: { backgroundLocation: location.state?.backgroundLocation || location },
                  });
                }
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}

export function UserSshKeyEditView({ user, keyEntity }: BaseProps & { keyEntity: SSHKeyEntity }) {
  const editKey = useEditSshKeyForUserMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const { businessGroupId } = useParams();

  const form = useForm<SshKeyFormValues>({
    resolver: zodResolver(sshKeySchema),
    defaultValues: {
      key: keyEntity.key,
      algorithm: keyEntity.algorithm || '',
      comments: keyEntity.comments || '',
    },
    mode: 'onBlur',
  });

  return (
    <div className='flex flex-col gap-3'>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(values => {
            editKey.mutate(
              {
                userId: user.id,
                keyId: keyEntity.identifier,
                changes: {
                  key: values.key.trim(),
                  algorithm: values.algorithm.trim(),
                  comments: values.comments?.trim() || '',
                },
              },
              {
                onSuccess() {
                  toast.success('SSH key updated');
                  if (businessGroupId) {
                    navigate(`/business-groups/${businessGroupId}/users/${user.id}/ssh-keys`, {
                      state: {
                        backgroundLocation: location.state?.backgroundLocation || location,
                      },
                    });
                  }
                },
                onError(err: unknown) {
                  showErrorToast(err);
                },
              },
            );
          })}
          className='flex flex-col gap-4'
        >
          <FormField
            control={form.control}
            name='key'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Public SSH Key</FormLabel>
                <FormControl>
                  <Textarea rows={5} placeholder='Paste public key' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='algorithm'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Algorithm</FormLabel>
                <FormControl>
                  <Input placeholder='e.g. ssh-rsa' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name='comments'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Comments</FormLabel>
                <FormControl>
                  <Input placeholder='Optional description' {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className='flex gap-2'>
            <Button size='sm' type='submit' disabled={editKey.isPending}>
              {editKey.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              variant='outline'
              size='sm'
              type='button'
              onClick={() => {
                if (businessGroupId) {
                  navigate(`/business-groups/${businessGroupId}/users/${user.id}/ssh-keys`, {
                    state: { backgroundLocation: location.state?.backgroundLocation || location },
                  });
                }
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
